import type { Meta } from "@/@types/paginated";
import { toast } from "@/components/ui/use-toast";
import {
  useCreateShippingRecipientMutation,
  useDeleteShippingRecipientMutation,
  useGetShippingRecipientsQuery,
  type ShippingRecipientListParams,
  useUpdateShippingRecipientMutation,
  type ShippingRecipientResponse,
} from "@/services/shipping-recipient";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { useCallback, useMemo, useRef } from "react";
import { SectorEmailsFilters } from "../filter/types";
import { SECTOR_EMAILS_ERRORS, SECTOR_EMAILS_TOASTS } from "../messages";
import { SectorRecipient, SectorRecipientInput } from "../types";

export interface RecipientActions {
  create: (input: SectorRecipientInput) => void;
  update: (id: number, input: SectorRecipientInput) => void;
  toggleActive: (recipient: SectorRecipient) => void;
  remove: (recipient: SectorRecipient) => void;
}

export interface RecipientsSource {
  rows: SectorRecipient[];
  meta: Meta;
  total: number;
  actions: RecipientActions;
  isLoading: boolean;
}

const ACTIVE_STATUS: ShippingRecipientResponse["status"] = "ACTIVE";

const INACTIVE_STATUS: ShippingRecipientResponse["status"] = "INACTIVE";

const EMPTY_ROWS: readonly ShippingRecipientResponse[] = [];

const EMPTY_META: Meta = {
  currentPage: 1,
  lastPage: 1,
  perPage: 0,
  total: 0,
  prev: null,
  next: null,
};

const toRecipient = (row: ShippingRecipientResponse): SectorRecipient => ({
  id: row.id,
  sector: row.sector,
  name: row.name,
  email: row.email,
  shipping_types: row.shipping_types,
  active: row.status === ACTIVE_STATUS,
  version: row.version,
});

export const toListParams = (
  filters: SectorEmailsFilters
): ShippingRecipientListParams => ({
  search: filters.search,
  sectors: filters.sectors,
  shipping_types: filters.shipping_types,
  active: filters.active,
  page: filters.meta.page,
  perPage: filters.meta.perPage,
  sort: filters.sorting.sort,
  direction: filters.sorting.direction,
});

export const useRecipientsSource = (
  filters: SectorEmailsFilters
): RecipientsSource => {
  const { data, isLoading } = useGetShippingRecipientsQuery(
    toListParams(filters)
  );

  const [createRecipient] = useCreateShippingRecipientMutation();
  const [updateRecipient] = useUpdateShippingRecipientMutation();
  const [deleteRecipient] = useDeleteShippingRecipientMutation();

  const rows: SectorRecipient[] = useMemo(
    (): SectorRecipient[] => (data?.data ?? EMPTY_ROWS).map(toRecipient),
    [data]
  );

  const notifyFailure = useCallback((error: unknown, fallback: string): void => {
    toast(SECTOR_EMAILS_TOASTS.FAILED(extractRtkErrorMessage(error, fallback)));
  }, []);

  const create = useCallback(
    (input: SectorRecipientInput): void => {
      void (async (): Promise<void> => {
        try {
          await createRecipient(input).unwrap();
          toast(SECTOR_EMAILS_TOASTS.CREATED());
        } catch (error: unknown) {
          notifyFailure(error, SECTOR_EMAILS_ERRORS.CREATE);
        }
      })();
    },
    [createRecipient, notifyFailure]
  );

  /**
   * `rows` como dependência do `update` reconstruiria as ações — e por consequência as colunas — a
   * cada refetch. A versão só é lida no momento do submit, então a leitura vai por ref.
   */
  const rowsRef = useRef<SectorRecipient[]>(rows);
  rowsRef.current = rows;

  const update = useCallback(
    (id: number, input: SectorRecipientInput): void => {
      const current: SectorRecipient | undefined = rowsRef.current.find(
        (recipient: SectorRecipient): boolean => recipient.id === id
      );

      void (async (): Promise<void> => {
        try {
          await updateRecipient({
            id,
            body: { ...input, version: current?.version ?? 1 },
          }).unwrap();
          toast(SECTOR_EMAILS_TOASTS.UPDATED());
        } catch (error: unknown) {
          notifyFailure(error, SECTOR_EMAILS_ERRORS.UPDATE);
        }
      })();
    },
    [updateRecipient, notifyFailure]
  );

  const toggleActive = useCallback(
    (recipient: SectorRecipient): void => {
      const active: boolean = !recipient.active;

      void (async (): Promise<void> => {
        try {
          await updateRecipient({
            id: recipient.id,
            body: {
              version: recipient.version,
              status: active ? ACTIVE_STATUS : INACTIVE_STATUS,
            },
          }).unwrap();
          toast(SECTOR_EMAILS_TOASTS.ACTIVE_CHANGED(active));
        } catch (error: unknown) {
          notifyFailure(error, SECTOR_EMAILS_ERRORS.CHANGE_STATUS);
        }
      })();
    },
    [updateRecipient, notifyFailure]
  );

  const remove = useCallback(
    (recipient: SectorRecipient): void => {
      void (async (): Promise<void> => {
        try {
          await deleteRecipient({
            id: recipient.id,
            version: recipient.version,
          }).unwrap();
          toast(SECTOR_EMAILS_TOASTS.DELETED());
        } catch (error: unknown) {
          notifyFailure(error, SECTOR_EMAILS_ERRORS.DELETE);
        }
      })();
    },
    [deleteRecipient, notifyFailure]
  );

  /**
   * O literal `{ create, update, ... }` nascia a cada render, e `useRecipientColumns` depende dele.
   * Colunas novas fazem o `flexRender` tratar cada `cell` como **tipo** novo: a tabela inteira
   * desmontava e remontava a cada render, não só quando o dado mudava.
   */
  const actions: RecipientActions = useMemo(
    (): RecipientActions => ({ create, update, toggleActive, remove }),
    [create, update, toggleActive, remove]
  );

  return {
    rows,
    meta: data?.meta ?? EMPTY_META,
    total: data?.meta.total_unfiltered ?? EMPTY_META.total,
    actions,
    isLoading,
  };
};
